import type { Tone } from "shared/tones";
import type { EvaluationItem, EvaluationPayload } from "../modal/evaluate_context";

export type CriterionKey =
  | "task_completion" | "team_player" | "time_management" | "positive_work_attitide"
  | "adaptable_and_flexible" | "ability_to_learn" | "problem_solving" | "punctuality";

export const CRITERIA: Array<{ key: CriterionKey; label: string }> = [
  { key: "task_completion", label: "Task Completion" },
  { key: "team_player", label: "Team Player" },
  { key: "time_management", label: "Time Management" },
  { key: "positive_work_attitide", label: "Positive Work Attitude" },
  { key: "adaptable_and_flexible", label: "Adaptable & Flexible" },
  { key: "ability_to_learn", label: "Ability to Learn" },
  { key: "problem_solving", label: "Problem Solving" },
  { key: "punctuality", label: "Punctuality" },
];

export const SCALE_REMARKS: Record<number, string> = {
  1: "Unsatisfactory",
  2: "Satisfactory",
  3: "Average",
  4: "Above Average",
  5: "Exceptional",
};

/** Scale word for a score; a decimal (3.6, 4.5) takes the word of the nearest whole number. */
export function scaleRemark(value: number): string {
  return SCALE_REMARKS[Math.min(5, Math.max(1, Math.round(value)))];
}

/** "4" for a whole score, "3.5" / "3.6" otherwise (never "3.5999999"). */
export function formatScore(value: number): string {
  return String(Math.round(value * 10) / 10);
}

/** 4+ good, 3-4 middling, below 3 needs attention. */
export function scoreTone(value: number): Tone {
  if (value >= 4) return "success";
  if (value >= 3) return "warning";
  return "danger";
}

/** Mean of the scored criteria (0 means "not scored" in stored data). */
export function evaluationAverage(e: Pick<EvaluationPayload, CriterionKey>): number | null {
  const vals = CRITERIA.map(({ key }) => e[key]).filter((v) => v > 0);
  return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
}

/** "YYYY-MM-DD" from an API timestamp, ignoring timezone shifts. */
export function dateOnly(value?: string): string {
  return value ? value.split("T")[0].split(" ")[0] : "";
}

export function formatDate(value?: string): string {
  const [y, m, d] = dateOnly(value).split("-").map(Number);
  if (!y || !m || !d) return "—";
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export function sortNewestFirst(items: EvaluationItem[]): EvaluationItem[] {
  return [...items].sort(
    (a, b) => dateOnly(b.created_at).localeCompare(dateOnly(a.created_at)) || b.evaluation_id - a.evaluation_id,
  );
}

/** The Monday-to-Sunday week containing `day` ("YYYY-MM-DD"), shifted by `weeks`; returns the Monday. */
export function shiftWeek(day: string, weeks: number): string {
  const [y, m, d] = day.split("-").map(Number);
  const date = new Date(y, m - 1, d + weeks * 7);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

/** "N days ago" / "today" for the overdue columns. */
export function formatDaysAgo(days: number | null): string {
  if (days === null) return "never";
  if (days <= 0) return "today";
  return days === 1 ? "1 day ago" : `${days} days ago`;
}
